import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'
import { useAdmin } from '../context/AdminContext'
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'
const PURPLE = '#7C3AED'

const PLAN_COLORS = {
  trial: AMBER, starter: MUTED, growth: BLUE,
  pro: PURPLE, business: GREEN, suspended: RED,
}

function Icon({ path, size = 15, color = MUTED, sw = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const IC = {
  mail:    'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  up:      'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
}

function KpiCard({ label, value, sub, accent, subUp, emoji }) {
  return (
    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, borderLeft: '3px solid ' + accent, padding: '16px 18px' }}>
      {emoji && <div style={{ fontSize: '20px', marginBottom: '6px' }}>{emoji}</div>}
      <div style={{ fontSize: '22px', fontWeight: 800, color: TEXT, letterSpacing: '-0.5px', marginBottom: '3px' }}>{value}</div>
      <div style={{ fontSize: '11.5px', fontWeight: 500, color: MUTED }}>{label}</div>
      {sub && <div style={{ fontSize: '11px', color: subUp === true ? GREEN : subUp === false ? RED : MUTED, marginTop: '2px' }}>{sub}</div>}
    </div>
  )
}

function InsightCard({ emoji, title, message, accent }) {
  return (
    <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, borderLeft: '3px solid ' + accent, padding: '14px 16px', display: 'flex', gap: '12px' }}>
      <span style={{ fontSize: '20px', flexShrink: 0 }}>{emoji}</span>
      <div>
        <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT, marginBottom: '3px' }}>{title}</div>
        <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.6 }}>{message}</div>
      </div>
    </div>
  )
}

function AdminAnalytics() {
  useAdmin()

  const [loading, setLoading]   = useState(true)
  const [income, setIncome]     = useState([])
  const [activeTab, setActiveTab] = useState('overview')
  const [stats, setStats] = useState({
    totalSellers: 0, trialSellers: 0, paidSellers: 0,
    suspendedSellers: 0, newThisMonth: 0, newLastMonth: 0,
    bdSellers: 0, intSellers: 0, verifiedSellers: 0,
    totalOrders: 0, totalGMV: 0, avgOrdersPerSeller: 0,
    planBreakdown: {}, categoryBreakdown: {},
    atRiskSellers: [], neverOrdered: [], expiringThisWeek: [],
    monthlySignups: [], monthlyOrders: [],
    retentionRate: 0, churnRate: 0, conversionRate: 0,
    topSellers: [], countryBreakdown: {},
  })

  useEffect(() => { fetchAll() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true)

    const [{ data: sellersData }, { data: ordersData }, { data: incomeData }] = await Promise.all([
      supabase.from('sellers').select('*').order('created_at', { ascending: false }),
      supabase.from('orders').select('seller_id, total_amount, created_at, status').order('created_at', { ascending: true }),
      supabase.from('admin_income').select('*').order('received_at', { ascending: false }),
    ])

    const allSellers = sellersData || []
    const allOrders  = ordersData  || []
    setIncome(incomeData || [])

    const now              = new Date()
    const startOfMonth     = new Date(now.getFullYear(), now.getMonth(), 1)
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const endOfLastMonth   = new Date(now.getFullYear(), now.getMonth(), 0)
    const oneWeekFromNow   = new Date(); oneWeekFromNow.setDate(now.getDate() + 7)
    const thirtyDaysAgo    = new Date(); thirtyDaysAgo.setDate(now.getDate() - 30)

    const paidPlans    = ['starter','growth','pro','business']
    const trialSellers = allSellers.filter(s => (s.subscription_plan || s.plan) === 'trial').length
    const paidSellers  = allSellers.filter(s => paidPlans.includes(s.subscription_plan || s.plan)).length
    const suspendedSellers = allSellers.filter(s => s.suspended).length
    const newThisMonth = allSellers.filter(s => new Date(s.created_at) >= startOfMonth).length
    const newLastMonth = allSellers.filter(s => { const d = new Date(s.created_at); return d >= startOfLastMonth && d <= endOfLastMonth }).length
    const bdSellers    = allSellers.filter(s => s.seller_mode !== 'international').length
    const intSellers   = allSellers.filter(s => s.seller_mode === 'international').length
    const verifiedSellers = allSellers.filter(s => s.verification_status === 'verified').length

    const totalGMV = allOrders.filter(o => o.status !== 'return').reduce((s, o) => s + Number(o.total_amount || 0), 0)

    const ordersBySeller = {}
    allOrders.forEach(o => { ordersBySeller[o.seller_id] = (ordersBySeller[o.seller_id] || 0) + 1 })

    const planBreakdown = {}
    allSellers.forEach(s => { const p = s.subscription_plan || s.plan || 'trial'; planBreakdown[p] = (planBreakdown[p] || 0) + 1 })

    const categoryBreakdown = {}
    allSellers.forEach(s => { if (s.category) categoryBreakdown[s.category] = (categoryBreakdown[s.category] || 0) + 1 })

    const countryBreakdown = {}
    allSellers.forEach(s => { const c = s.country || (s.seller_mode === 'bangladesh' ? 'Bangladesh' : 'Unknown'); countryBreakdown[c] = (countryBreakdown[c] || 0) + 1 })

    const atRiskSellers = allSellers.filter(s => {
      const lastOrder = allOrders.filter(o => o.seller_id === s.id).slice(-1)[0]
      const daysSinceJoin = (now - new Date(s.created_at)) / 86400000
      if (daysSinceJoin < 7) return false
      if (!lastOrder) return true
      return new Date(lastOrder.created_at) < thirtyDaysAgo
    }).slice(0, 10)

    const neverOrdered = allSellers.filter(s => !ordersBySeller[s.id]).slice(0, 10)

    const expiringThisWeek = allSellers.filter(s => {
      const end = s.subscription_end || s.trial_ends_at
      if (!end) return false
      const d = new Date(end)
      return d >= now && d <= oneWeekFromNow
    })

    // Monthly trend — last 12 months
    const monthlySignups = []
    const monthlyOrders  = []
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    for (let i = 11; i >= 0; i--) {
      const d    = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1)
      const signups  = allSellers.filter(s => { const cd = new Date(s.created_at); return cd >= d && cd < next }).length
      const orders   = allOrders.filter(o => { const cd = new Date(o.created_at); return cd >= d && cd < next }).length
      const revenue  = allOrders.filter(o => { const cd = new Date(o.created_at); return cd >= d && cd < next && o.status !== 'return' }).reduce((s, o) => s + Number(o.total_amount || 0), 0)
      monthlySignups.push({ month: MONTHS[d.getMonth()], signups })
      monthlyOrders.push({ month: MONTHS[d.getMonth()], orders, revenue: Math.round(revenue) })
    }

    const topSellers = allSellers.map(s => ({
      ...s,
      orderCount: ordersBySeller[s.id] || 0,
      gmv: allOrders.filter(o => o.seller_id === s.id && o.status !== 'return').reduce((s, o) => s + Number(o.total_amount || 0), 0),
    })).sort((a, b) => b.orderCount - a.orderCount).slice(0, 10)

    // Conversion: of sellers who joined before last month, how many are now paid
    const eligible  = allSellers.filter(s => new Date(s.created_at) <= startOfLastMonth)
    const converted = eligible.filter(s => paidPlans.includes(s.subscription_plan || s.plan)).length
    const conversionRate = eligible.length > 0 ? Math.round((converted / eligible.length) * 100) : 0
    const retentionRate  = allSellers.length > 0 ? Math.round(((allSellers.length - suspendedSellers) / allSellers.length) * 100) : 0

    setStats({
      totalSellers: allSellers.length, trialSellers, paidSellers, suspendedSellers,
      newThisMonth, newLastMonth, bdSellers, intSellers, verifiedSellers,
      totalOrders: allOrders.length, totalGMV,
      avgOrdersPerSeller: allSellers.length > 0 ? (allOrders.length / allSellers.length).toFixed(1) : 0,
      planBreakdown, categoryBreakdown, countryBreakdown,
      atRiskSellers, neverOrdered, expiringThisWeek,
      monthlySignups, monthlyOrders, topSellers,
      retentionRate, churnRate: 100 - retentionRate, conversionRate,
    })
    setLoading(false)
  }

  const fmt = (n) => {
    const v = Math.abs(Math.round(n || 0))
    if (v >= 1000000) return '৳' + (v / 1000000).toFixed(1) + 'M'
    if (v >= 100000)  return '৳' + Math.round(v / 1000) + 'k'
    return '৳' + v.toLocaleString()
  }

  const thisMonthincome = income.filter(i => {
    const d = new Date(i.received_at)
    const n = new Date()
    return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear()
  }).reduce((s, i) => s + Number(i.amount || 0), 0)

  // Smart coaching insights
  const insights = []
  if (stats.conversionRate < 30) insights.push({ emoji: '⚠️', title: 'Low trial conversion (' + stats.conversionRate + '%) — action needed', message: 'Consider follow-up emails, onboarding calls, or a limited-time discount to push conversions.', accent: RED })
  else if (stats.conversionRate >= 50) insights.push({ emoji: '🚀', title: 'Excellent conversion rate — ' + stats.conversionRate + '%!', message: 'Well above industry average. Keep delivering value and this will compound.', accent: GREEN })
  if (stats.expiringThisWeek.length > 0) insights.push({ emoji: '⏰', title: stats.expiringThisWeek.length + ' subscriptions expiring this week', message: 'Send renewal reminders to: ' + stats.expiringThisWeek.map(s => s.shop_name).slice(0, 3).join(', ') + (stats.expiringThisWeek.length > 3 ? ' and ' + (stats.expiringThisWeek.length - 3) + ' more.' : '.'), accent: AMBER })
  if (stats.neverOrdered.length > 5) insights.push({ emoji: '😴', title: stats.neverOrdered.length + ' sellers never placed an order', message: 'A personal onboarding message could activate them. Consider a getting-started email sequence.', accent: PURPLE })
  if (stats.atRiskSellers.length > 0) insights.push({ emoji: '🔔', title: stats.atRiskSellers.length + ' sellers inactive 30+ days', message: 'They may churn at renewal. A check-in call or feature highlight email could re-engage them.', accent: '#F97316' })
  if (stats.newThisMonth > stats.newLastMonth) insights.push({ emoji: '📈', title: 'Signups up — ' + stats.newThisMonth + ' this month vs ' + stats.newLastMonth + ' last month', message: 'Growth is positive. Double down on what is working.', accent: BLUE })
  if (stats.intSellers > 0 && stats.intSellers < stats.bdSellers * 0.1) insights.push({ emoji: '🌍', title: 'International seller opportunity', message: 'Only ' + stats.intSellers + ' international sellers. International sellers pay in USD — consider targeted diaspora marketing.', accent: '#6366F1' })

  const TABS = ['overview', 'growth', 'sellers', 'actions']

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>
          <style>{`
            @media(max-width:960px){ .ana-kpi{grid-template-columns:1fr 1fr!important} .ana-charts{grid-template-columns:1fr!important} }
            @media(max-width:600px){ .ana-kpi{grid-template-columns:1fr!important} }
          `}</style>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Analytics</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>Platform performance — sellers, orders, revenue and growth</div>
            </div>
            <button onClick={fetchAll} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: MUTED, background: WHITE, border: '1px solid ' + BORDER, padding: '7px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
              <Icon path={IC.refresh} size={13} color={MUTED} />
              Refresh
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', padding: '4px', background: WHITE, border: '1px solid ' + BORDER, borderRadius: '10px', marginBottom: '18px', width: 'fit-content' }}>
            {TABS.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab ? BLUE : 'transparent', color: activeTab === tab ? WHITE : MUTED, fontFamily: 'Space Grotesk, sans-serif', textTransform: 'capitalize', transition: 'all 0.12s', whiteSpace: 'nowrap' }}>
                {tab}
              </button>
            ))}
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: MUTED, fontSize: '13px' }}>Loading analytics...</div>
          )}

          {!loading && activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Hero MRR banner */}
              <div style={{ background: 'linear-gradient(135deg, #1877F2 0%, #7C3AED 100%)', borderRadius: '14px', padding: '24px', color: WHITE }}>
                <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>This month — subscription income</div>
                <div style={{ fontSize: '32px', fontWeight: 800, letterSpacing: '-1px', marginBottom: '6px' }}>{fmt(thisMonthincome)}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginTop: '16px' }}>
                  {[
                    { label: 'Paid sellers',   value: stats.paidSellers },
                    { label: 'On trial',        value: stats.trialSellers },
                    { label: 'Conversion',      value: stats.conversionRate + '%' },
                    { label: 'Retention',       value: stats.retentionRate + '%' },
                  ].map(s => (
                    <div key={s.label} style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '18px', fontWeight: 800 }}>{s.value}</div>
                      <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Insights */}
              {insights.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {insights.map((ins, i) => <InsightCard key={i} {...ins} />)}
                </div>
              )}

              {/* KPI row 1 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }} className="ana-kpi">
                <KpiCard label="Total sellers"    value={stats.totalSellers}   sub={'+' + stats.newThisMonth + ' this month'} subUp={stats.newThisMonth >= stats.newLastMonth} accent={BLUE}   emoji="🏪" />
                <KpiCard label="Paid subscribers" value={stats.paidSellers}    sub={stats.conversionRate + '% conversion'}    subUp={stats.conversionRate >= 30}               accent={GREEN}  emoji="💳" />
                <KpiCard label="Total orders"     value={stats.totalOrders.toLocaleString()} sub={'GMV ' + fmt(stats.totalGMV)}                                               accent={PURPLE} emoji="📦" />
                <KpiCard label="Retention"        value={stats.retentionRate + '%'} sub={'Churn: ' + stats.churnRate + '%'} subUp={stats.retentionRate >= 90}                  accent={AMBER}  emoji="🔒" />
              </div>

              {/* KPI row 2 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }} className="ana-kpi">
                <KpiCard label="On trial"          value={stats.trialSellers}       sub="Free access"      accent={AMBER}   emoji="⏳" />
                <KpiCard label="Verified"          value={stats.verifiedSellers}    sub="With badge"       accent={GREEN}   emoji="✅" />
                <KpiCard label="Bangladesh"        value={stats.bdSellers}          sub="BDT sellers"      accent={BLUE}    emoji="🇧🇩" />
                <KpiCard label="International"     value={stats.intSellers}         sub="USD sellers"      accent={PURPLE}  emoji="🌍" />
              </div>

              {/* Plan breakdown */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '14px' }}>Sellers by plan</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {Object.entries(stats.planBreakdown).sort((a, b) => b[1] - a[1]).map(([plan, count]) => {
                    const pct = stats.totalSellers > 0 ? Math.round((count / stats.totalSellers) * 100) : 0
                    return (
                      <div key={plan}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: PLAN_COLORS[plan] || MUTED }} />
                            <span style={{ fontSize: '12.5px', color: TEXT, textTransform: 'capitalize', fontWeight: 600 }}>{plan}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '12px', color: MUTED }}>{count} sellers</span>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: TEXT, width: '36px', textAlign: 'right' }}>{pct}%</span>
                          </div>
                        </div>
                        <div style={{ height: '6px', background: BG, borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: pct + '%', background: PLAN_COLORS[plan] || MUTED, borderRadius: '3px', transition: 'width 0.4s' }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Country breakdown */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }} className="ana-charts">
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '14px' }}>Sellers by country</div>
                  {Object.entries(stats.countryBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([country, count]) => {
                    const pct = stats.totalSellers > 0 ? Math.round((count / stats.totalSellers) * 100) : 0
                    return (
                      <div key={country} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', color: TEXT }}>{country}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '80px', height: '5px', background: BG, borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: pct + '%', background: BLUE, borderRadius: '3px' }} />
                          </div>
                          <span style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED, width: '28px', textAlign: 'right' }}>{count}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '14px' }}>Sellers by category</div>
                  {Object.entries(stats.categoryBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([cat, count]) => {
                    const pct = stats.totalSellers > 0 ? Math.round((count / stats.totalSellers) * 100) : 0
                    return (
                      <div key={cat} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>{cat}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          <div style={{ width: '60px', height: '5px', background: BG, borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: pct + '%', background: PURPLE, borderRadius: '3px' }} />
                          </div>
                          <span style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED, width: '20px', textAlign: 'right' }}>{count}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {!loading && activeTab === 'growth' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Signup trend */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Monthly signups — last 12 months</div>
                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>Seller acquisition momentum</div>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={stats.monthlySignups} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: MUTED }} width={28} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '10px', fontSize: '12px', fontFamily: 'Space Grotesk' }} formatter={v => [v, 'Signups']} />
                      <Bar dataKey="signups" fill={BLUE} radius={[5, 5, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Orders + Revenue trend */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Monthly orders — last 12 months</div>
                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>Platform activity across all sellers</div>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={stats.monthlyOrders} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: MUTED }} width={28} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '10px', fontSize: '12px', fontFamily: 'Space Grotesk' }} formatter={(v, name) => [name === 'orders' ? v : fmt(v), name === 'orders' ? 'Orders' : 'Revenue']} />
                      <Bar dataKey="orders" fill={GREEN} radius={[5, 5, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Revenue trend */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Monthly GMV — last 12 months</div>
                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>Gross merchandise value across all sellers</div>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={stats.monthlyOrders} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: MUTED }} width={50} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? '৳' + Math.round(v / 1000) + 'k' : '৳' + v} />
                      <Tooltip contentStyle={{ border: '1px solid ' + BORDER, borderRadius: '10px', fontSize: '12px', fontFamily: 'Space Grotesk' }} formatter={v => [fmt(v), 'GMV']} />
                      <Line type="monotone" dataKey="revenue" stroke={PURPLE} strokeWidth={2.5} dot={{ fill: PURPLE, r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Growth KPIs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }} className="ana-kpi">
                <KpiCard label="New this month"   value={stats.newThisMonth}     sub={'vs ' + stats.newLastMonth + ' last month'} subUp={stats.newThisMonth >= stats.newLastMonth} accent={BLUE} />
                <KpiCard label="Avg orders/seller" value={stats.avgOrdersPerSeller} sub="All time average"                                                                         accent={GREEN} />
                <KpiCard label="Total GMV"         value={fmt(stats.totalGMV)}    sub="All sellers all time"                                                                       accent={PURPLE} />
                <KpiCard label="Suspended"         value={stats.suspendedSellers} sub="Inactive accounts"   subUp={stats.suspendedSellers === 0}                                   accent={RED} />
              </div>
            </div>
          )}

          {!loading && activeTab === 'sellers' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Top 10 sellers */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>Top 10 sellers by order volume</div>
                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>Most active sellers on the platform</div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                    <thead>
                      <tr style={{ background: BG }}>
                        {['#', 'Shop', 'Region', 'Plan', 'Orders', 'GMV'].map(h => (
                          <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid ' + BORDER }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stats.topSellers.map((s, i) => (
                        <tr key={s.id} style={{ borderBottom: '1px solid #F5F5F5' }}
                          onMouseEnter={e => e.currentTarget.style.background = BG}
                          onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                          <td style={{ padding: '10px 16px', fontSize: '13px' }}>
                            {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : <span style={{ fontSize: '11px', color: MUTED, fontWeight: 700 }}>{i + 1}</span>}
                          </td>
                          <td style={{ padding: '10px 16px' }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT }}>{s.shop_name}</div>
                            <div style={{ fontSize: '11px', color: MUTED }}>{s.email}</div>
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: '12px', color: MUTED }}>{s.seller_mode === 'international' ? '🌍 Intl' : '🇧🇩 BD'}</td>
                          <td style={{ padding: '10px 16px' }}>
                            <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: (PLAN_COLORS[s.subscription_plan || s.plan] || MUTED) + '18', color: PLAN_COLORS[s.subscription_plan || s.plan] || MUTED, textTransform: 'capitalize' }}>
                              {s.subscription_plan || s.plan || 'trial'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: '13px', fontWeight: 800, color: BLUE }}>{s.orderCount.toLocaleString()}</td>
                          <td style={{ padding: '10px 16px', fontSize: '13px', fontWeight: 800, color: GREEN }}>{fmt(s.gmv)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* At-risk sellers */}
              {stats.atRiskSellers.length > 0 && (
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER, background: '#FFF7ED' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#C2410C' }}>At-risk sellers — inactive 30+ days ({stats.atRiskSellers.length})</div>
                    <div style={{ fontSize: '11px', color: '#EA580C', marginTop: '2px' }}>Consider a personal check-in to re-engage</div>
                  </div>
                  {stats.atRiskSellers.map((s, i) => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderBottom: i < stats.atRiskSellers.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{s.shop_name}</div>
                        <div style={{ fontSize: '11px', color: MUTED }}>{s.email}</div>
                      </div>
                      <a href={'mailto:' + s.email} style={{ fontSize: '12px', fontWeight: 600, color: '#C2410C', background: '#FFF7ED', border: '1px solid #FED7AA', padding: '5px 12px', borderRadius: '7px', textDecoration: 'none', flexShrink: 0 }}>
                        Email
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {/* Never ordered */}
              {stats.neverOrdered.length > 0 && (
                <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER, background: '#FAF5FF' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: PURPLE }}>Never activated — {stats.neverOrdered.length} sellers</div>
                    <div style={{ fontSize: '11px', color: PURPLE, marginTop: '2px', opacity: 0.8 }}>Signed up but never placed an order</div>
                  </div>
                  {stats.neverOrdered.map((s, i) => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderBottom: i < stats.neverOrdered.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{s.shop_name}</div>
                        <div style={{ fontSize: '11px', color: MUTED }}>Joined {new Date(s.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      </div>
                      <a href={'mailto:' + s.email} style={{ fontSize: '12px', fontWeight: 600, color: PURPLE, background: '#FAF5FF', border: '1px solid #DDD6FE', padding: '5px 12px', borderRadius: '7px', textDecoration: 'none', flexShrink: 0 }}>
                        Onboard
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && activeTab === 'actions' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

              {/* Expiring this week */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid ' + BORDER, background: '#FEF2F2' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: RED }}>Expiring this week — {stats.expiringThisWeek.length} sellers</div>
                  <div style={{ fontSize: '11px', color: RED, marginTop: '2px', opacity: 0.8 }}>Send renewal reminders before they lapse</div>
                </div>
                {stats.expiringThisWeek.length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', color: MUTED, fontSize: '12px' }}>No subscriptions expiring this week</div>
                ) : stats.expiringThisWeek.map((s, i) => {
                  const end       = new Date(s.subscription_end || s.trial_ends_at)
                  const daysLeft  = Math.ceil((end - new Date()) / 86400000)
                  return (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 18px', borderBottom: i < stats.expiringThisWeek.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{s.shop_name}</div>
                        <div style={{ fontSize: '11px', color: MUTED }}>{s.subscription_plan || s.plan} · expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</div>
                      </div>
                      <a href={'mailto:' + s.email + '?subject=Your PageConnect subscription is expiring soon'}
                        style={{ fontSize: '12px', fontWeight: 600, color: RED, background: '#FEF2F2', border: '1px solid #FECACA', padding: '5px 12px', borderRadius: '7px', textDecoration: 'none', flexShrink: 0 }}>
                        Remind
                      </a>
                    </div>
                  )
                })}
              </div>

              {/* All insights */}
              {insights.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '4px' }}>All smart insights</div>
                  {insights.map((ins, i) => <InsightCard key={i} {...ins} />)}
                </div>
              )}

              {/* Quick actions */}
              <div style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '16px 18px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, marginBottom: '14px' }}>Quick bulk actions</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    { label: 'Email all trial sellers', sub: stats.trialSellers + ' sellers on free trial', href: 'mailto:?bcc=&subject=Upgrade your PageConnect plan', color: AMBER },
                    { label: 'Email all paid sellers',  sub: stats.paidSellers + ' active subscribers',     href: 'mailto:?bcc=&subject=Your PageConnect update',      color: GREEN },
                    { label: 'Email all sellers',       sub: 'All ' + stats.totalSellers + ' sellers',      href: 'mailto:?bcc=&subject=Important update from PageConnect', color: BLUE },
                  ].map(action => (
                    <a key={action.label} href={action.href}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', border: '1px solid ' + BORDER, textDecoration: 'none', background: BG, cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = WHITE}
                      onMouseLeave={e => e.currentTarget.style.background = BG}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: action.color }}>{action.label}</div>
                        <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{action.sub}</div>
                      </div>
                      <Icon path={IC.mail} color={action.color} size={16} sw={2} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default AdminAnalytics